import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  ColumnsPanelTrigger, DataGridPremium, GridColDef,
  Toolbar, ToolbarButton,
  gridRowGroupingSanitizedModelSelector,
  useGridApiContext,
  useGridSelector,
} from "@mui/x-data-grid-premium";
import ViewColumnIcon from "@mui/icons-material/ViewColumn";
import RefreshIcon from "@mui/icons-material/Refresh";
import {
  Box, Checkbox,
  FormControl, FormControlLabel, FormGroup,
  InputLabel, MenuItem, Select, TextField, Tooltip, Typography,
} from "@mui/material";
import MoveUpIcon from "@mui/icons-material/MoveUp";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown, faChevronUp, faXmark, faFilePdf } from "@fortawesome/free-solid-svg-icons";
import FilterIcon from "assets/icons/tune.svg?react";
import DownloadIcon from "assets/icons/download.svg?react";
import InfoIcon from "assets/icons/info_icon.svg?react";
import PDFViewer from "components/PDFViewer";
import Popper from "components/Popper";
import { randomString } from "utils/randomString";
import { WPK_GMINY } from "data/wpk_gminy";
import { ZOIS_BO_KEY } from "components/Settings/DictionariesTab/WpkGminyTab";
import { plGridLocales } from "utils/muiGridLocales";
import { useOpeningBalance, useTurnovers } from "api/accountingService";
import useGridRowSelection from "hooks/useGridRowSelection";
import {
  generateTrialBalancePdf,
  TrialBalancePdfRow,
} from "utils/generateTrialBalancePdf";
import styles from "./styles.module.scss";

interface Props {
  searchText: string;
  setSubtitle: (s: string) => void;
}

type AccountKategoria = "bilansowe" | "pozabilansowe" | "wynikowe";

interface AccountRow {
  id: string;
  path: string[];
  code: string;
  name: string;
  kategoria: AccountKategoria;
  boWn: number;
  boMa: number;
  obrWn: number;
  obrMa: number;
  cumWn: number;
  cumMa: number;
}

type AccountScope = "all" | "niezero" | "bilansowe" | "pozabilansowe" | "wynikowe";

const SCOPE_LABELS: Record<AccountScope, string> = {
  all: "Wszystkie",
  niezero: "Konta z saldem niezerowym",
  bilansowe: "Konta bilansowe",
  pozabilansowe: "Konta pozabilansowe",
  wynikowe: "Konta wynikowe",
};

const SCOPE_OPTIONS: AccountScope[] = ["all", "niezero", "bilansowe", "pozabilansowe", "wynikowe"];

// ── Konta — kryteria wielowyboru ──────────────────────────────────────────────

type KontoKryterium =
  | "bilansowe"
  | "bil_aktywne" | "bil_pasywne" | "bil_aktywno_pasywne"
  | "pozabilansowe" | "saldo_niezerowe"
  | "syntetyczne" | "analityczne" | "wynikowe";

interface KontaItem {
  key: KontoKryterium;
  label: string;
  parent: KontoKryterium | null;
}

const KONTA_ITEMS: KontaItem[] = [
  { key: "bilansowe",          label: "Bilansowe",           parent: null },
  { key: "bil_aktywne",        label: "Aktywne",             parent: "bilansowe" },
  { key: "bil_pasywne",        label: "Pasywne",             parent: "bilansowe" },
  { key: "bil_aktywno_pasywne",label: "Aktywno-pasywne",     parent: "bilansowe" },
  { key: "pozabilansowe",      label: "Pozabilansowe",       parent: null },
  { key: "saldo_niezerowe",    label: "Saldo niezerowe",     parent: null },
  { key: "syntetyczne",        label: "Syntetyczne",         parent: null },
  { key: "analityczne",        label: "Analityczne",         parent: null },
  { key: "wynikowe",           label: "Wynikowe",            parent: null },
];

const KONTA_CHILDREN: KontoKryterium[] = ["bil_aktywne", "bil_pasywne", "bil_aktywno_pasywne"];

// ── Zespoły z WPK Gminy (drugi poziom drzewa) ─────────────────────────────────
const ZESPOLY = WPK_GMINY.flatMap((root) =>
  root.children.map((z) => ({ id: z.id, code: z.code, label: z.label })),
);

// ── Nowa struktura filtrów ────────────────────────────────────────────────────

interface TrialBalanceFilters {
  bookingFrom: string;
  bookingTo: string;
  zespol: string;                   // "" = wszystkie; code = wybrany zespół
  kontaCriteria: KontoKryterium[];  // OR między elementami; puste = brak filtra
}

interface SavedFilter {
  Id: string;
  Name: string;
  Criteria: TrialBalanceFilters;
}

const LS_KEY = "trialBalanceRegister_filters";

const EMPTY_FILTERS: TrialBalanceFilters = {
  bookingFrom: "",
  bookingTo: "",
  zespol: "",
  kontaCriteria: [],
};

// ── Dokumenty źródłowe (mock) ─────────────────────────────────────────────

interface SourceDocument {
  id: string;
  dataDekretu: string;
  nrDokumentu: string;
  kwotaWn: number;
  kwotaMa: number;
  podmiotNazwa: string;
  podmiotNip: string;
  przedmiot: string;
  pdfUrl: string;
}

const MOCK_SOURCE_DOCS: Record<string, SourceDocument[]> = {};

// ── Budowanie wierszy ZOiS z danych BO (importowanych z FK) ──────────────────

interface IZoisBoEntry { code: string; name: string; boWn: number; boMa: number; }

const normalizeCode = (raw: string) => raw.replace(/\//g, "-");

const buildAccountRows = (entries: IZoisBoEntry[]): AccountRow[] => {
  const normalized = entries.map((e) => ({ ...e, code: normalizeCode(e.code) }));
  const sorted = [...normalized].sort((a, b) =>
    a.code.localeCompare(b.code, undefined, { numeric: true }),
  );
  const codeSet = new Set(sorted.map((e) => e.code));
  return sorted.map(({ code, name, boWn, boMa }) => {
    const path: string[] = [];
    if (code.includes("-")) {
      const parts = code.split("-");
      for (let i = 1; i < parts.length; i++) {
        const prefix = parts.slice(0, i).join("-");
        if (codeSet.has(prefix)) path.push(prefix);
      }
    } else {
      for (let len = 1; len < code.length; len++) {
        const prefix = code.substring(0, len);
        if (codeSet.has(prefix)) path.push(prefix);
      }
    }
    path.push(code);
    return {
      id: `zois-${code}`,
      path,
      code,
      name,
      kategoria: "bilansowe" as AccountKategoria,
      boWn,
      boMa,
      obrWn: 0,
      obrMa: 0,
      cumWn: boWn,
      cumMa: boMa,
    };
  });
};

// Nałóż obroty (z silnika księgowania) na wiersze ZOiS: dopisz brakujące konta,
// dodaj obroty do konta i wszystkich jego syntetyk (ancestrów), przelicz narastająco.
const overlayTurnovers = (
  rows: AccountRow[],
  turnovers?: { account: string; obrWn: number; obrMa: number }[],
): AccountRow[] => {
  if (!turnovers || turnovers.length === 0) return rows;
  const entries: IZoisBoEntry[] = rows.map((r) => ({
    code: r.code,
    name: r.name,
    boWn: r.boWn,
    boMa: r.boMa,
  }));
  const present = new Set(rows.map((r) => r.code));
  turnovers.forEach((t) => {
    const code = normalizeCode(t.account);
    if (code && !present.has(code)) {
      entries.push({ code, name: "", boWn: 0, boMa: 0 });
      present.add(code);
    }
  });
  const merged = buildAccountRows(entries);
  const byCode = new Map(merged.map((r) => [r.code, r]));
  turnovers.forEach((t) => {
    const code = normalizeCode(t.account);
    const leaf = byCode.get(code);
    const ancestors = leaf ? leaf.path : [code];
    ancestors.forEach((a) => {
      const row = byCode.get(a);
      if (row) {
        row.obrWn += t.obrWn;
        row.obrMa += t.obrMa;
      }
    });
  });
  merged.forEach((r) => {
    r.cumWn = r.boWn + r.obrWn;
    r.cumMa = r.boMa + r.obrMa;
  });
  return merged;
};

const loadBoRows = (): AccountRow[] => {
  try {
    const raw = localStorage.getItem(ZOIS_BO_KEY);
    if (!raw) return [];
    const { entries } = JSON.parse(raw) as { entries: IZoisBoEntry[] };
    return buildAccountRows(entries ?? []);
  } catch {
    return [];
  }
};

const fmt = (v: number): string =>
  v
    ? v.toLocaleString("pl-PL", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    : "";

const saldoWn = (r: AccountRow) => Math.max(0, r.boWn + r.obrWn - (r.boMa + r.obrMa));
const saldoMa = (r: AccountRow) => Math.max(0, r.boMa + r.obrMa - (r.boWn + r.obrWn));

const matchesScope = (r: AccountRow, scope: AccountScope): boolean => {
  switch (scope) {
    case "all": return true;
    case "niezero": return saldoWn(r) > 0 || saldoMa(r) > 0;
    case "bilansowe": return r.kategoria === "bilansowe";
    case "pozabilansowe": return r.kategoria === "pozabilansowe";
    case "wynikowe": return r.kategoria === "wynikowe";
  }
};

const matchesKontoKryterium = (r: AccountRow, k: KontoKryterium): boolean => {
  switch (k) {
    case "bilansowe":
    case "bil_aktywne":
    case "bil_pasywne":
    case "bil_aktywno_pasywne": return r.kategoria === "bilansowe";
    case "pozabilansowe":       return r.kategoria === "pozabilansowe";
    case "saldo_niezerowe":     return saldoWn(r) > 0 || saldoMa(r) > 0;
    case "syntetyczne":         return r.path.length === 1;
    case "analityczne":         return r.path.length > 1;
    case "wynikowe":            return r.kategoria === "wynikowe";
  }
};

const filterRows = (
  rows: AccountRow[],
  q: string,
  f: TrialBalanceFilters,
  scope: AccountScope,
): AccountRow[] => {
  const lower = q.toLowerCase();
  const matchIds = new Set<string>();

  rows.forEach((r) => {
    const textMatch =
      !q || r.code.toLowerCase().includes(lower) || r.name.toLowerCase().includes(lower);

    // Zespół: filtruj po pierwszej cyfrze kodu konta
    const zespolMatch =
      !f.zespol || r.code.startsWith(f.zespol.charAt(0));

    // Konta kryteria: OR
    const kontaMatch =
      f.kontaCriteria.length === 0 ||
      f.kontaCriteria.some((k) => matchesKontoKryterium(r, k));

    // Zakres (toolbar)
    const scopeMatch = matchesScope(r, scope);

    if (textMatch && zespolMatch && kontaMatch && scopeMatch) {
      r.path.forEach((seg) => matchIds.add(seg));
    }
  });

  const noFilters = !q && !f.zespol && f.kontaCriteria.length === 0 && scope === "all";
  if (matchIds.size === 0 && noFilters) return rows;
  return rows.filter((r) => matchIds.has(r.code));
};

type RangePreset = "rok" | "kwartal" | "miesiac";

const CURRENT_YEAR = new Date().getFullYear();
const YEAR_OPTIONS = Array.from({ length: 7 }, (_, i) => CURRENT_YEAR - i);

const toISO = (d: Date) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};
const todayISO = () => toISO(new Date());

// Oblicza zakres od/do dla danego presetu i roku. Dla bieżącego roku "do" = dziś,
// dla lat przeszłych "do" = ostatni dzień okresu (31 gru / koniec kwartału / koniec miesiąca).
const getPresetRange = (preset: RangePreset, year: number): [string, string] => {
  const now = new Date();
  const isCurrent = year === CURRENT_YEAR;
  const today = todayISO();

  if (preset === "rok") {
    return [`${year}-01-01`, isCurrent ? today : `${year}-12-31`];
  }
  if (preset === "kwartal") {
    const qStart = Math.floor(now.getMonth() / 3) * 3;
    const from = toISO(new Date(year, qStart, 1));
    const to = isCurrent ? today : toISO(new Date(year, qStart + 3, 0));
    return [from, to];
  }
  // miesiac
  const month = now.getMonth();
  const from = toISO(new Date(year, month, 1));
  const to = isCurrent ? today : toISO(new Date(year, month + 1, 0));
  return [from, to];
};

const presetLabels: Record<RangePreset, string> = {
  rok: "Rok",
  kwartal: "Kwartał",
  miesiac: "Miesiąc",
};

const plDate = (iso: string) =>
  iso ? new Date(iso).toLocaleDateString("pl-PL") : "—";

// ── Komponent panelu filtru ────────────────────────────────────────────────

interface FilterPanelProps {
  filters: TrialBalanceFilters;
  onChange: (f: TrialBalanceFilters) => void;
  onClose: () => void;
  onReset: () => void;
  onSave: (name: string) => void;
}

const FilterPanel = ({ filters, onChange, onClose, onReset, onSave }: FilterPanelProps) => {
  const [showNameInput, setShowNameInput] = useState(false);
  const [filterName, setFilterName] = useState("");

  const set = <K extends keyof TrialBalanceFilters>(key: K, val: TrialBalanceFilters[K]) =>
    onChange({ ...filters, [key]: val });

  const hasAny =
    !!filters.bookingFrom || !!filters.bookingTo ||
    !!filters.zespol || filters.kontaCriteria.length > 0;

  const handleSave = () => {
    if (!showNameInput) { setShowNameInput(true); return; }
    onSave(filterName.trim() || "Nowy filtr");
    setShowNameInput(false);
    setFilterName("");
  };

  // ── Konta: obsługa parent-child ──────────────────────────────────────────
  const konta = new Set(filters.kontaCriteria);

  const toggleKonto = (key: KontoKryterium) => {
    const next = new Set(konta);
    if (key === "bilansowe") {
      // zaznacz/odznacz wszystkie dzieci
      const anyChild = KONTA_CHILDREN.some((c) => next.has(c));
      if (anyChild) {
        next.delete("bilansowe");
        KONTA_CHILDREN.forEach((c) => next.delete(c));
      } else {
        next.add("bilansowe");
        KONTA_CHILDREN.forEach((c) => next.add(c));
      }
    } else {
      next.has(key) ? next.delete(key) : next.add(key);
    }
    set("kontaCriteria", [...next]);
  };

  const bilansParentChecked = KONTA_CHILDREN.every((c) => konta.has(c));
  const bilansParentIndet = !bilansParentChecked && KONTA_CHILDREN.some((c) => konta.has(c));

  return createPortal(
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.panel} onClick={(e) => e.stopPropagation()}>
        <div className={styles.panelHeader}>
          <span className={styles.panelTitle}>Filtry</span>
          <div className={styles.panelHeaderActions}>
            {hasAny && (
              <button className={styles.resetBtn} onClick={onReset}>Wyczyść</button>
            )}
            <button className={styles.closeBtn} onClick={onClose}>
              <FontAwesomeIcon icon={faXmark} />
            </button>
          </div>
        </div>

        <div className={styles.panelBody}>

          {/* ── Okres księgowania ── */}
          <div className={styles.filterSection}>
            <span className={styles.filterLabel}>Okres księgowania</span>
            <div className={styles.filterRangeRow}>
              <TextField
                label="Od"
                type="date"
                size="small"
                InputLabelProps={{ shrink: true }}
                value={filters.bookingFrom}
                onChange={(e) => set("bookingFrom", e.target.value)}
              />
              <TextField
                label="Do"
                type="date"
                size="small"
                InputLabelProps={{ shrink: true }}
                value={filters.bookingTo}
                onChange={(e) => set("bookingTo", e.target.value)}
              />
            </div>
          </div>

          {/* ── Zespół ── */}
          <div className={styles.filterSection}>
            <span className={styles.filterLabel}>Zespół</span>
            <FormControl size="small" fullWidth>
              <InputLabel>Wybierz zespół</InputLabel>
              <Select
                label="Wybierz zespół"
                value={filters.zespol}
                onChange={(e) => set("zespol", e.target.value)}
              >
                <MenuItem value=""><em>Wszystkie</em></MenuItem>
                {ZESPOLY.map((z) => (
                  <MenuItem key={z.id} value={z.code}>
                    <span className={styles.zespolCode}>{z.code}</span>
                    &nbsp;{z.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>

          {/* ── Konta ── */}
          <div className={styles.filterSection}>
            <span className={styles.filterLabel}>Konta</span>
            <FormGroup>
              {KONTA_ITEMS.map((item) => {
                const isParent = item.key === "bilansowe";
                const isChild = !!item.parent;
                return (
                  <FormControlLabel
                    key={item.key}
                    sx={{ ml: isChild ? 2 : 0, my: -0.4 }}
                    control={
                      <Checkbox
                        size="small"
                        checked={isParent ? bilansParentChecked : konta.has(item.key)}
                        indeterminate={isParent ? bilansParentIndet : false}
                        onChange={() => toggleKonto(item.key)}
                      />
                    }
                    label={
                      <span
                        className={`${styles.kontoLabel} ${isParent ? styles.kontoLabelParent : ""}`}
                      >
                        {item.label}
                      </span>
                    }
                  />
                );
              })}
            </FormGroup>
          </div>

          {/* ── Zapis filtru ── */}
          <div className={styles.filterSaveSection}>
            {showNameInput && (
              <input
                className={styles.filterNameInput}
                placeholder="Nazwa filtra"
                value={filterName}
                onChange={(e) => setFilterName(e.target.value)}
                autoFocus
              />
            )}
            <button className={styles.filterSaveBtn} onClick={handleSave}>
              Zapisz filtr
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
};

// ── Panel szczegółów konta ────────────────────────────────────────────────

const KATEGORIA_LABELS: Record<AccountKategoria, string> = {
  bilansowe: "Bilansowe",
  pozabilansowe: "Pozabilansowe",
  wynikowe: "Wynikowe",
};

interface AccountDetailPanelProps {
  account: AccountRow;
  onClose: () => void;
}

const AccountDetailPanel = ({ account, onClose }: AccountDetailPanelProps) => {
  const docs = MOCK_SOURCE_DOCS[account.code] ?? [];
  const [openPdfDoc, setOpenPdfDoc] = useState<SourceDocument | null>(null);
  const [pdfBlob, setPdfBlob]       = useState<Blob | null>(null);
  const [pdfLoading, setPdfLoading] = useState(false);

  const handleOpenPdf = async (doc: SourceDocument) => {
    if (openPdfDoc?.id === doc.id) {
      setOpenPdfDoc(null);
      setPdfBlob(null);
      return;
    }
    setOpenPdfDoc(doc);
    setPdfBlob(null);
    setPdfLoading(true);
    try {
      const res = await fetch(doc.pdfUrl);
      if (!res.ok) throw new Error();
      setPdfBlob(await res.blob());
    } catch {
      setPdfBlob(null);
    } finally {
      setPdfLoading(false);
    }
  };

  return createPortal(
    <div className={styles.overlay} onClick={onClose}>

      {/* ── Podgląd PDF — slide do lewej krawędzi panelu ── */}
      {openPdfDoc && (
        <div className={styles.pdfPreview} onClick={(e) => e.stopPropagation()}>
          {pdfLoading ? (
            <div className={styles.pdfPlaceholder}>Ładowanie podglądu…</div>
          ) : pdfBlob ? (
            <PDFViewer
              file={pdfBlob}
              title={openPdfDoc.nrDokumentu}
              showCloseButton
              onCloseButton={() => { setOpenPdfDoc(null); setPdfBlob(null); }}
            />
          ) : (
            <div className={styles.pdfPlaceholder}>
              Podgląd niedostępny
            </div>
          )}
        </div>
      )}

      <div
        className={`${styles.panel} ${styles.panelWide}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className={styles.panelHeader}>
          <span className={styles.panelTitle}>Lista dokumentów źródłowych</span>
          <button className={styles.closeBtn} onClick={onClose}>
            <FontAwesomeIcon icon={faXmark} />
          </button>
        </div>

        <div className={styles.panelBody}>

          {/* ── Dane konta ── */}
          <div className={styles.detailSection}>
            <span className={styles.detailSectionTitle}>Dane konta</span>
            <div className={styles.accountMetaGrid}>
              <span className={styles.accountMetaLabel}>Symbol:</span>
              <span className={styles.accountMetaValue}>{account.code}</span>
              <span className={styles.accountMetaLabel}>Kategoria:</span>
              <span className={styles.accountMetaValue}>{KATEGORIA_LABELS[account.kategoria]}</span>

              <span className={styles.accountMetaLabel}>Nazwa:</span>
              <span className={`${styles.accountMetaValue} ${styles.accountMetaSpan}`}>{account.name}</span>

              <span className={styles.accountMetaLabel}>Obroty Wn:</span>
              <span className={styles.accountMetaValue}>{fmt(account.obrWn) || "—"}</span>
              <span className={styles.accountMetaLabel}>Obroty Ma:</span>
              <span className={styles.accountMetaValue}>{fmt(account.obrMa) || "—"}</span>

              <span className={styles.accountMetaLabel}>Saldo Wn:</span>
              <span className={styles.accountMetaValue}>{fmt(saldoWn(account)) || "—"}</span>
              <span className={styles.accountMetaLabel}>Saldo Ma:</span>
              <span className={styles.accountMetaValue}>{fmt(saldoMa(account)) || "—"}</span>
            </div>
          </div>

          {/* ── Lista dokumentów ── */}
          <div className={styles.detailSection}>
            <span className={styles.detailSectionTitle}>Lista dokumentów źródłowych</span>
            {docs.length === 0 ? (
              <span className={styles.savedFiltersEmpty}>Brak dokumentów źródłowych</span>
            ) : (
              <div className={styles.docList}>
                {docs.map((doc) => {
                  const isWn = doc.kwotaWn !== 0;
                  const isActive = openPdfDoc?.id === doc.id;
                  return (
                    <Tooltip
                      key={doc.id}
                      placement="left"
                      enterDelay={300}
                      title={
                        <>
                          <div style={{ fontWeight: 600, marginBottom: 2 }}>{doc.podmiotNazwa}</div>
                          <div>NIP: {doc.podmiotNip}</div>
                          <div style={{ marginTop: 5, color: "rgba(255,255,255,0.82)" }}>{doc.przedmiot}</div>
                        </>
                      }
                    >
                      <div className={`${styles.docRow} ${isActive ? styles.docRowActive : ""}`}>
                        <span className={styles.docDate}>{plDate(doc.dataDekretu)}</span>
                        <span className={styles.docNum}>{doc.nrDokumentu}</span>
                        <span className={isWn ? styles.docAmountWn : styles.docAmountMa}>
                          {isWn ? fmt(doc.kwotaWn) : fmt(doc.kwotaMa)}
                          <span className={styles.docSide}>{isWn ? " Wn" : " Ma"}</span>
                        </span>
                        <Tooltip title={isActive ? "Zamknij podgląd" : "Podgląd dokumentu"} placement="left">
                          <button
                            className={`${styles.docPdfBtn} ${isActive ? styles.docPdfBtnActive : ""}`}
                            onClick={(e) => { e.stopPropagation(); void handleOpenPdf(doc); }}
                          >
                            <FontAwesomeIcon icon={faFilePdf} />
                          </button>
                        </Tooltip>
                      </div>
                    </Tooltip>
                  );
                })}
              </div>
            )}
          </div>

        </div>
      </div>
    </div>,
    document.body,
  );
};

// ── Główny komponent ───────────────────────────────────────────────────────

const TrialBalanceRegister = ({ searchText, setSubtitle }: Props) => {
  // ── Rok (potrzebny przed hookami API) ─────────────────────────────────────
  const [selectedYear, setSelectedYear] = useState(CURRENT_YEAR);

  // ── Dane BO z backendu ────────────────────────────────────────────────────
  const { data: boApiData, isLoading: boApiLoading, refetch: refetchBo } = useOpeningBalance(selectedYear);

  const [rawData, setRawData] = useState<AccountRow[]>(() => loadBoRows());

  // Synchronizuj rawData z danymi z BE — ale TYLKO gdy BE ma dane.
  // Nie nadpisujemy rawData pustym wynikiem (np. gdy GET odpowie przed zakończeniem POST).
  useEffect(() => {
    if (boApiLoading) return;
    if (boApiData?.entries?.length) {
      setRawData(buildAccountRows(
        boApiData.entries.map((e) => ({ code: e.code, name: e.name ?? "", boWn: e.boWn, boMa: e.boMa })),
      ));
    }
    // Jeśli BE puste: zachowaj aktualny rawData (może mieć dane z lokalnego importu lub localStorage)
  }, [boApiData, boApiLoading]);

  useEffect(() => {
    const handler = () => void refetchBo();
    window.addEventListener("zois_bo_updated", handler);
    return () => window.removeEventListener("zois_bo_updated", handler);
  }, [refetchBo]);

  const [preset, setPreset] = useState<RangePreset | null>("rok");
  const [from, setFrom] = useState(`${CURRENT_YEAR}-01-01`);
  const [to, setTo] = useState(todayISO());
  const [scopeFilter, setScopeFilter] = useState<AccountScope>("all");
  const [filterOpen, setFilterOpen] = useState(false);
  const [colFilters, setColFilters] = useState<TrialBalanceFilters>(EMPTY_FILTERS);
  const [savedFilters, setSavedFilters] = useState<SavedFilter[]>([]);
  const [showSavedFilters, setShowSavedFilters] = useState(false);
  const [showPeriodPopup, setShowPeriodPopup] = useState(false);
  const savedFiltersRef = useRef<HTMLDivElement>(null);
  const periodBtnRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    try { setSavedFilters(JSON.parse(localStorage.getItem(LS_KEY) || "[]")); } catch { /* */ }
  }, []);

  const persistFilters = (list: SavedFilter[]) => {
    setSavedFilters(list);
    localStorage.setItem(LS_KEY, JSON.stringify(list));
  };

  const handleSaveFilter = (name: string) => {
    persistFilters([...savedFilters, { Id: randomString(), Name: name, Criteria: colFilters }]);
  };

  const applyNamedFilter = (f: SavedFilter) => {
    setColFilters(f.Criteria);
    setShowSavedFilters(false);
  };

  const removeNamedFilter = (id: string) =>
    persistFilters(savedFilters.filter((x) => x.Id !== id));

  const { selectedRowIds, rowsRef, handleRowClick } = useGridRowSelection(
    (id) => id === "__total__",
  );

  const [selectedAccountId, setSelectedAccountId] = useState<string | null>(null);
  const [downloading, setDownloading] = useState(false);

  const handleDownload = async () => {
    if (downloading) return;
    setDownloading(true);
    try {
      const pdfRows: TrialBalancePdfRow[] = rows.map((r) => ({
        code: r.code,
        name: r.name,
        depth: r.path.length - 1,
        boWn: r.boWn,
        boMa: r.boMa,
        obrWn: r.obrWn,
        obrMa: r.obrMa,
        cumWn: r.cumWn,
        cumMa: r.cumMa,
        saldoWn: saldoWn(r),
        saldoMa: saldoMa(r),
      }));
      pdfRows.push({
        code: "",
        name: "RAZEM",
        depth: 0,
        boWn: totalRow.boWn,
        boMa: totalRow.boMa,
        obrWn: totalRow.obrWn,
        obrMa: totalRow.obrMa,
        cumWn: totalRow.cumWn,
        cumMa: totalRow.cumMa,
        saldoWn: saldoWn(totalRow),
        saldoMa: saldoMa(totalRow),
        isTotal: true,
      });
      await generateTrialBalancePdf({
        rows: pdfRows,
        from,
        to,
        year: selectedYear,
        scopeLabel: SCOPE_LABELS[scopeFilter],
      });
    } finally {
      setDownloading(false);
    }
  };

  const applyPreset = (p: RangePreset, year = selectedYear) => {
    const [f, t] = getPresetRange(p, year);
    setPreset(p);
    setFrom(f);
    setTo(t);
  };

  const handleYearChange = (year: number) => {
    setSelectedYear(year);
    if (preset) applyPreset(preset, year);
  };

  const activeCount = useMemo(() => {
    let n = 0;
    if (colFilters.bookingFrom) n++;
    if (colFilters.bookingTo) n++;
    if (colFilters.zespol) n++;
    n += colFilters.kontaCriteria.length;
    return n;
  }, [colFilters]);

  const filterTooltipContent = useMemo(() => {
    const lines: string[] = [];
    if (colFilters.bookingFrom) lines.push(`Okres od: ${plDate(colFilters.bookingFrom)}`);
    if (colFilters.bookingTo)   lines.push(`Okres do: ${plDate(colFilters.bookingTo)}`);
    if (colFilters.zespol) {
      const z = ZESPOLY.find((z) => z.code === colFilters.zespol);
      lines.push(`Zespół: ${z ? `${z.code} – ${z.label}` : colFilters.zespol}`);
    }
    colFilters.kontaCriteria.forEach((k) => {
      const item = KONTA_ITEMS.find((i) => i.key === k);
      if (item) lines.push(`Konto: ${item.label}`);
    });
    if (lines.length === 0) return "Filtry";
    return (
      <>
        <div style={{ fontWeight: 600, marginBottom: 3 }}>Aktywne filtry:</div>
        {lines.map((l, i) => <div key={i}>• {l}</div>)}
      </>
    );
  }, [colFilters]);

  // Obroty z silnika księgowania (zaksięgowane dekrety) nałożone na drzewo BO.
  const { data: turnovers } = useTurnovers(from, to);
  const mergedData = useMemo(
    () => overlayTurnovers(rawData, turnovers),
    [rawData, turnovers],
  );

  const rows = useMemo(
    () => filterRows(mergedData, searchText.trim(), colFilters, scopeFilter),
    [mergedData, searchText, colFilters, scopeFilter],
  );
  rowsRef.current = rows; // potrzebne dla useGridRowSelection (zakres Shift)

  const totalRow = useMemo<AccountRow>(() => {
    const synth = rows.filter((r) => r.path.length === 1);
    const sum = (k: keyof AccountRow) =>
      synth.reduce((acc, r) => acc + (r[k] as number), 0);
    return {
      id: "__total__",
      path: ["__total__"],
      code: "",
      name: "RAZEM",
      kategoria: "bilansowe" as AccountKategoria,
      boWn: sum("boWn"),
      boMa: sum("boMa"),
      obrWn: sum("obrWn"),
      obrMa: sum("obrMa"),
      cumWn: sum("cumWn"),
      cumMa: sum("cumMa"),
    };
  }, [rows]);

  useEffect(() => {
    setSubtitle(`Okres: ${plDate(from)} – ${plDate(to)}`);
  }, [setSubtitle, from, to]);

  const amountCol = (
    field: string,
    headerName: string,
    getValue: (r: AccountRow) => number,
  ): GridColDef => ({
    field,
    headerName,
    width: 116,
    headerAlign: "right",
    align: "right",
    sortable: false,
    valueGetter: (_v, row: AccountRow) => getValue(row),
    valueFormatter: (v: number) => fmt(v),
  });

  const columns: GridColDef[] = [
    { field: "name", headerName: "Nazwa konta", flex: 1, minWidth: 220, sortable: false },
    amountCol("boWn", "BO Wn", (r) => r.boWn),
    amountCol("boMa", "BO Ma", (r) => r.boMa),
    amountCol("obrWn", "Obroty Wn", (r) => r.obrWn),
    amountCol("obrMa", "Obroty Ma", (r) => r.obrMa),
    amountCol("cumWn", "Obroty Wn narast.", (r) => r.cumWn),
    amountCol("cumMa", "Obroty Ma narast.", (r) => r.cumMa),
    amountCol("saldoWn", "Saldo Wn", saldoWn),
    amountCol("saldoMa", "Saldo Ma", saldoMa),
    {
      field: "__detail",
      headerName: "",
      width: 36,
      sortable: false,
      disableColumnMenu: true,
      renderCell: (params) => {
        if ((params.row as AccountRow).id === "__total__") return null;
        return (
          <Tooltip title="Dokumenty źródłowe" placement="left">
            <button
              className={styles.infoBtn}
              onClick={(e) => {
                e.stopPropagation();
                setSelectedAccountId((params.row as AccountRow).id);
              }}
            >
              <InfoIcon style={{ width: 18, height: 18 }} />
            </button>
          </Tooltip>
        );
      },
    },
  ];

  const TrialBalanceToolbar = () => {
    const gridApiRef = useGridApiContext();
    const rowGroupingModel = useGridSelector(gridApiRef, gridRowGroupingSanitizedModelSelector);

    return (
      <Toolbar>
        <div className={styles.mergedToolbar}>
          <div className={styles.groupingSection}>
            <MoveUpIcon fontSize="small" color="action" sx={{ mr: 0.75, flexShrink: 0 }} />
            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
              {rowGroupingModel.length === 0 && (
                <Typography variant="body2" color="textSecondary" noWrap>
                  Przeciągnij kolumnę tutaj aby zgrupować
                </Typography>
              )}
            </Box>
          </div>

          <div className={styles.toolbarRight}>
            <Tooltip title={boApiLoading ? "Ładowanie…" : "Odśwież dane z bazy"} placement="bottom">
              <button
                className={styles.filterBtn}
                onClick={() => void refetchBo()}
                disabled={boApiLoading}
              >
                <RefreshIcon
                  fontSize="small"
                  className={boApiLoading ? styles.refreshSpinning : undefined}
                />
              </button>
            </Tooltip>

            <Tooltip title="Pobierz PDF" placement="bottom">
              <button
                className={`${styles.filterBtn} ${downloading ? styles.filterBtnActive : ""}`}
                onClick={handleDownload}
                disabled={downloading}
              >
                <DownloadIcon />
              </button>
            </Tooltip>

            <div className={styles.savedFiltersWrap} ref={periodBtnRef}>
              <div
                className={styles.savedFiltersBtn}
                onClick={() => setShowPeriodPopup((v) => !v)}
              >
                Okres
                <FontAwesomeIcon icon={showPeriodPopup ? faChevronUp : faChevronDown} />
              </div>
            </div>
            {showPeriodPopup && (
              <Popper
                anchorEl={periodBtnRef.current}
                open={showPeriodPopup}
                setOpen={setShowPeriodPopup}
                placement="bottom-start"
                overlay={false}
              >
                <div className={styles.periodDropdown}>
                  <div className={styles.periodRow}>
                    <FormControl size="small" sx={{ minWidth: 88 }}>
                      <InputLabel>Rok</InputLabel>
                      <Select
                        label="Rok"
                        value={selectedYear}
                        onChange={(e) => handleYearChange(Number(e.target.value))}
                      >
                        {YEAR_OPTIONS.map((y) => (
                          <MenuItem key={y} value={y}>{y}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <FormControl size="small" sx={{ minWidth: 120 }}>
                      <InputLabel>Preset</InputLabel>
                      <Select
                        label="Preset"
                        value={preset ?? ""}
                        onChange={(e) => {
                          const val = e.target.value as RangePreset;
                          if (val) applyPreset(val);
                        }}
                      >
                        {(Object.keys(presetLabels) as RangePreset[]).map((p) => (
                          <MenuItem key={p} value={p}>{presetLabels[p]}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </div>
                  <div className={styles.periodRow}>
                    <TextField
                      label="Okres od"
                      type="date"
                      size="small"
                      InputLabelProps={{ shrink: true }}
                      value={from}
                      onChange={(e) => { setFrom(e.target.value); setPreset(null); }}
                    />
                    <TextField
                      label="Okres do"
                      type="date"
                      size="small"
                      InputLabelProps={{ shrink: true }}
                      value={to}
                      onChange={(e) => { setTo(e.target.value); setPreset(null); }}
                    />
                  </div>
                </div>
              </Popper>
            )}

            <FormControl size="small" sx={{ minWidth: 220 }}>
              <InputLabel>Zakres</InputLabel>
              <Select
                label="Zakres"
                value={scopeFilter}
                onChange={(e) => setScopeFilter(e.target.value as AccountScope)}
              >
                {SCOPE_OPTIONS.map((s) => (
                  <MenuItem key={s} value={s}>{SCOPE_LABELS[s]}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <div className={styles.savedFiltersWrap} ref={savedFiltersRef}>
              <div
                className={styles.savedFiltersBtn}
                onClick={() => setShowSavedFilters((v) => !v)}
              >
                Twoje filtry
                <FontAwesomeIcon icon={showSavedFilters ? faChevronUp : faChevronDown} />
              </div>
            </div>
            {showSavedFilters && (
              <Popper
                anchorEl={savedFiltersRef.current}
                open={showSavedFilters}
                setOpen={setShowSavedFilters}
                placement="bottom-end"
                overlay={false}
              >
                <div className={styles.savedFiltersDropdown}>
                  {savedFilters.length === 0 && (
                    <div className={styles.savedFiltersEmpty}>Brak zapisanych filtrów</div>
                  )}
                  {savedFilters.map((f) => (
                    <div
                      key={f.Id}
                      className={styles.savedFilterItem}
                      onClick={() => applyNamedFilter(f)}
                    >
                      <span className={styles.savedFilterName}>{f.Name}</span>
                      <button
                        className={styles.savedFilterDel}
                        onClick={(e) => { e.stopPropagation(); removeNamedFilter(f.Id); }}
                      >
                        ✕
                      </button>
                    </div>
                  ))}
                </div>
              </Popper>
            )}

            <Tooltip title={filterTooltipContent} placement="bottom-end">
              <button
                className={`${styles.filterBtn} ${activeCount > 0 ? styles.filterBtnActive : ""}`}
                onClick={() => setFilterOpen(true)}
              >
                <FilterIcon />
                {activeCount > 0 && (
                  <span className={styles.filterBadge}>{activeCount}</span>
                )}
              </button>
            </Tooltip>

            <Tooltip title="Kolumny" placement="bottom-end">
              <ColumnsPanelTrigger
                render={
                  <ToolbarButton
                    sx={{
                      border: "1px solid rgba(0, 0, 0, 0.23)",
                      borderRadius: "4px",
                      padding: "4px 8px",
                      color: "#5f5c71",
                      minWidth: "unset",
                      "&:hover": {
                        borderColor: "#5d49c7",
                        background: "rgba(93, 73, 199, 0.04)",
                        color: "#5d49c7",
                      },
                    }}
                  />
                }
              >
                <ViewColumnIcon fontSize="small" />
              </ColumnsPanelTrigger>
            </Tooltip>
          </div>
        </div>
      </Toolbar>
    );
  };

  return (
    <div className={styles.wrapper}>
      <div className={styles.gridArea}>
        <DataGridPremium
          treeData
          getTreeDataPath={(row: AccountRow) => row.path}
          slots={{ toolbar: TrialBalanceToolbar }}
          showToolbar
          groupingColDef={{
            headerName: "Symbol konta",
            minWidth: 120,
            maxWidth: 400,
            flex: 0.8,
          }}
          rows={rows}
          columns={columns}
          getRowId={(r) => r.id}
          pinnedRows={{ bottom: [totalRow] }}
          defaultGroupingExpansionDepth={searchText.trim() ? -1 : 0}
          density="compact"
          disableRowSelectionOnClick
          rowSelectionModel={selectedRowIds}
          onRowClick={handleRowClick}
          hideFooter
          localeText={plGridLocales}
          getRowClassName={(p) =>
            (p.row as AccountRow).path.length === 1 ? styles.syntheticRow : ""
          }
        />
      </div>

      {filterOpen && (
        <FilterPanel
          filters={colFilters}
          onChange={setColFilters}
          onClose={() => setFilterOpen(false)}
          onReset={() => setColFilters(EMPTY_FILTERS)}
          onSave={handleSaveFilter}
        />
      )}

      {selectedAccountId && (() => {
        const acc = rows.find((r) => r.id === selectedAccountId)
          ?? rawData.find((r) => r.id === selectedAccountId);
        return acc ? (
          <AccountDetailPanel
            account={acc}
            onClose={() => setSelectedAccountId(null)}
          />
        ) : null;
      })()}
    </div>
  );
};

export default TrialBalanceRegister;
